import { useEffect, type ReactNode } from "react";
import { cn } from "../ui/cn";

// ---------------------------------------------------------------------------
// Panel lateral de detalle (CC-CONSOLE#1595): se abre a la derecha al elegir
// un registro, como el panel de propiedades de una consola de monitoreo.
// Escape o el fondo lo cierran. En el teléfono ocupa todo el ancho.
// ---------------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Avatar o icono a la izquierda del título. */
  leading?: ReactNode;
  /** Chips o acciones bajo el título. */
  meta?: ReactNode;
  /** Pestañas u otra barra fija bajo la cabecera. */
  toolbar?: ReactNode;
  children: ReactNode;
  closeLabel: string;
  testId?: string;
}

export default function SideDrawer({ open, onClose, title, subtitle, leading, meta, toolbar, children, closeLabel, testId = "side-drawer" }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testId}>
      <button type="button" aria-label={closeLabel} className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside role="dialog" aria-modal="true" className={cn("relative flex h-full w-full max-w-xl flex-col border-l border-line bg-app shadow-2xl")}>
        <header className="border-b border-line bg-card px-5 py-4">
          <div className="flex items-start gap-3">
            {leading}
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-lg font-bold text-content">{title}</h2>
              {subtitle && <p className="truncate text-sm text-content-secondary">{subtitle}</p>}
            </div>
            <button type="button" onClick={onClose} aria-label={closeLabel} className="rounded-lg p-1.5 text-content-secondary hover:bg-subtle hover:text-content">
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </button>
          </div>
          {meta && <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div>}
        </header>
        {toolbar && <div className="border-b border-line bg-card px-5">{toolbar}</div>}
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </aside>
    </div>
  );
}
